/* eslint-disable react-native/no-inline-styles */
/**
 * @author Granite Bagas
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import PropTypes from 'prop-types';

import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import ModalFull from '@components/modal/ModalFull';

import { isWeb } from '@global/screens';
import { getAttibutes, isVn } from '@global/helpers';
import { getProductsFromEs, getProductsCountFromEs } from '@api/ms-b2b';
import { getProductCount, getProducts } from '@api/ms-catalog';
import {
  searchProductListingItemsCount,
  searchProductListingItems,
} from '@api/ms-order';
import tableApi from '@/hooks/tableApi';
import Colors from '@global/colors';
import {
  REQUEST_PRODUCT_MODAL_PRODUCT,
  REQUEST_PRODUCT_MODAL_PRODUCT_CATALOG,
} from '@/constants/table-name';
import { useDispatch, useSelector } from 'react-redux';
import { selectParams, paramsReseted, getCurrentParams } from '@/store/params';
import store from '@/store';

const propTypes = {
  show: PropTypes.bool,
  isCatalog: PropTypes.bool,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  customerId: PropTypes.string,
};

const defaultProps = {
  show: false,
  isCatalog: true,
  onClose: () => {},
  onChange: () => {},
  customerId: '',
};

const HEADER_DEKSTOP = [
  {
    head: 'Product Image',
    body: 'image',
    type: 'image',
  },
  {
    head: 'Internal Reference',
    body: 'reference',
  },
  {
    head: 'Name',
    body: 'name',
  },
  {
    head: 'Attribute Value',
    body: 'attributes',
  },
];

const defaultPhoto = require('@/assets/img/default/user-default.png');

const ModalProduct = (props) => {
  const {
    show = false,
    onClose = () => {},
    onChange = () => {},
    isCatalog = true,
    customerId = '',
  } = props;

  const dispatch = useDispatch();

  const hasInitializedRef = useRef(false);

  const paramsState = useSelector(
    selectParams(
      isCatalog
        ? REQUEST_PRODUCT_MODAL_PRODUCT_CATALOG
        : REQUEST_PRODUCT_MODAL_PRODUCT,
    ),
  );

  const [searchValue, setSearchValue] = useState('');

  const [list, setList] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  const products = isCatalog
    ? tableApi(
        getProductCount,
        getProducts,
        getProductsCountFromEs,
        getProductsFromEs,
      )
    : tableApi(searchProductListingItemsCount, searchProductListingItems);

  const handleClose = () => {
    onClose();
    dispatch(
      paramsReseted({
        key: isCatalog
          ? REQUEST_PRODUCT_MODAL_PRODUCT_CATALOG
          : REQUEST_PRODUCT_MODAL_PRODUCT,
      }),
    );
    setList([]);
    products.reset();
    setSearchValue('');
  };

  const callBackRowPress = (item) => {
    handleClose();
    onChange(item);
  };

  const onSearchTable = async (val, isMobile) => {
    if (isMobile) {
      setLoadingMore(true);
    }
    if (val) {
      setSearchValue(val);
    }
    const params = getCurrentParams(
      store.getState(),
      isCatalog
        ? REQUEST_PRODUCT_MODAL_PRODUCT_CATALOG
        : REQUEST_PRODUCT_MODAL_PRODUCT,
    );
    const finalParams = { ...params };

    if (!isCatalog) {
      finalParams.filter = {
        ...finalParams.filter,
        year: new Date().getFullYear(),
        customer_id: customerId,
      };
    }

    if (finalParams?.filter?.keyword) {
      const stored = finalParams.filter.keyword;
      finalParams.filter.keyword = stored?.$regex ?? stored;
    }

    const hasFilter = Object.keys(finalParams?.filter || {}).length
      ? true
      : false;
    const response = await products.request(finalParams, null, hasFilter);
    if (response) {
      setList(generateData(response));
      if (isMobile) {
        setLoadingMore(false);
      }
    }
  };

  const getImage = (image) => {
    let img = '';
    if (image?.length) {
      for (let index = 0; index < image.length; index++) {
        if (index === 0) {
          img = image[index].url;
        }
        if (image[index].is_cover) {
          img = image[index].url;
        }
      }
    }
    return img;
  };

  const generateData = (data) => {
    const products = [];
    for (let index = 0; index < data.length; index++) {
      const prod = data[index];
      if (isCatalog) {
        const combinations = prod.combinations || [];
        for (let index2 = 0; index2 < combinations.length; index2++) {
          const prodCombi = combinations[index2];
          const combi = { name: prod.name };
          combi.reference = prodCombi.reference || '';
          combi.brand = prod.brand || {};
          combi.ean_no = prodCombi.ean_no || '';
          combi.id = prod.id;
          combi.image = getImage(prodCombi.images);
          combi.attributes = getAttibutes(prodCombi.attributes);
          combi.product_combination_id = prodCombi._id;
          combi.ref_name = '[' + combi.reference + '] ' + combi.name;
          combi.category = prod.default_category;
          if (
            isVn() ? prodCombi.is_active_in_b2b_vn : prodCombi.is_active_in_b2b
          ) {
            products.push(combi);
          }
        }
      } else {
        const combi = {
          name: prod.name,
          reference: prod.combination.reference,
          brand: prod.brand,
          ean_no: prod.combination.ean_no,
          id: prod.id,
          image: prod.combination.image,
          attributes: getAttibutes(prod.combination.attributes),
          product_combination_id: prod.combination.id,
          ref_name: '[' + prod.combination.reference + '] ' + prod.name,
        };
        products.push(combi);
      }
    }
    return products;
  };

  useEffect(() => {
    if (show) {
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        dispatch(
          paramsReseted({
            key: isCatalog
              ? REQUEST_PRODUCT_MODAL_PRODUCT_CATALOG
              : REQUEST_PRODUCT_MODAL_PRODUCT,
          }),
        );
        if (!isCatalog || !isWeb) {
          onSearchTable();
        }
      }
    } else {
      hasInitializedRef.current = false;
    }
  }, [isCatalog, customerId, show]);

  const renderItem = ({ data, index }) => (
    <TouchableOpacity
      onPress={() => callBackRowPress(data)}
      key={'list-product-' + data.reference}
      style={styles.tableRow}>
      <View style={styles.productItem}>
        <View style={{ width: '20%' }}>
          <Image
            source={{ uri: `${data.image ? data.image : defaultPhoto}` }}
            style={{ width: 36, height: 36 }}
            width={36}
            height={36}
            defaultSource={defaultPhoto}
          />
        </View>
        <View style={{ width: '80%' }}>
          <SociollaText type="medium" style={styles.productBold}>
            {data.brand?.name}
          </SociollaText>
          <SociollaText style={styles.productNormal}>{data.name}</SociollaText>
          <SociollaText type="medium" style={styles.productBold}>
            {data.reference}
          </SociollaText>
          <SociollaText style={styles.productNormalLast}>
            {data.attributes}
          </SociollaText>
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <ModalFull
      title={'Select Product'}
      show={show}
      onClose={handleClose}
      width={isWeb ? 1000 : '100%'}>
      <View style={styles.tableContainer}>
        {isWeb && (
          <ScrollView
            contentContainerStyle={{
              paddingBottom: 40,
              maxHeight: 600,
            }}>
            <Table
              name={
                isCatalog
                  ? REQUEST_PRODUCT_MODAL_PRODUCT_CATALOG
                  : REQUEST_PRODUCT_MODAL_PRODUCT
              }
              filterName={
                isCatalog
                  ? REQUEST_PRODUCT_MODAL_PRODUCT_CATALOG
                  : REQUEST_PRODUCT_MODAL_PRODUCT
              }
              structures={HEADER_DEKSTOP}
              loading={products.loading}
              data={list}
              searchKey="keyword"
              searchPlaceholder="Search by Name, Reference, EAN"
              searchValue={searchValue}
              hasNoHide={true}
              hasCallback={true}
              totalData={products.count}
              skip={paramsState.skip}
              limit={paramsState.limit}
              onSearch={(val) => onSearchTable(val)}
              showFilter={false}
              onClickRow={({ data }) => callBackRowPress(data)}
            />
          </ScrollView>
        )}
        {!isWeb && (
          <TableMobile
            useLegendList
            name={
              isCatalog
                ? REQUEST_PRODUCT_MODAL_PRODUCT_CATALOG
                : REQUEST_PRODUCT_MODAL_PRODUCT
            }
            filterName={
              isCatalog
                ? REQUEST_PRODUCT_MODAL_PRODUCT_CATALOG
                : REQUEST_PRODUCT_MODAL_PRODUCT
            }
            searchKey="keyword"
            placeholder="Search by Name, Reference, EAN"
            searchValue={searchValue}
            loading={products.loading && list.length == 0}
            onSearch={(val) => onSearchTable(val, true)}
            skip={paramsState.skip}
            limit={paramsState.limit}
            tableData={list}
            totalData={products.count}
            loadingPagination={loadingMore}
            withLoadMore
            withFetch
            noResetOnRefresh
            onLoadMore={() => onSearchTable(searchValue, true)}
            showSearch={true}
            renderItem={renderItem}
          />
        )}
      </View>
    </ModalFull>
  );
};

const styles = StyleSheet.create({
  tableRow: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    height: 110,
    width: '100%',
    flex: 1,
    borderBottomColor: Colors.grey9,
    borderBottomWidth: 1,
    paddingHorizontal: 20,
    ...(!isWeb
      ? {
          paddingVertical: 15,
        }
      : {}),
  },
  tableContainer: isWeb
    ? {
        backgroundColor: Colors.grey11,
        paddingHorizontal: 35,
        paddingVertical: 20,
        height: '100%',
        maxHeight: 600,
      }
    : { flex: 1, backgroundColor: Colors.white },
  productItem: { flexDirection: 'row', width: '100%', alignItems: 'center' },
  productBold: { fontSize: 12, color: Colors.black1, paddingBottom: 8 },
  productNormal: { fontSize: 12, color: Colors.black5, paddingBottom: 8 },
  productNormalLast: { fontSize: 12, color: Colors.black5 },
});

ModalProduct.propTypes = propTypes;
ModalProduct.defaultProps = defaultProps;

export default React.memo(ModalProduct, (prev, next) => prev == next);
